import Peer, { DataConnection } from 'peerjs';

export type P2PMessageType =
  | 'START_GAME'
  | 'SYNC_STATE'
  | 'REQUEST_SYNC'
  | 'TURN_ACTION'
  | 'CHAT_MSG';

export interface P2PMessage {
  type: P2PMessageType | 'HANDSHAKE' | 'DISCONNECT';
  payload: any;
}

export type P2PEventHandler = (payload: any) => void;

export class P2PManager {
  private peer: Peer | null = null;
  private connection: DataConnection | null = null;
  public isHost: boolean = false;
  public peerId: string | null = null;
  public playerName: string = '';
  public opponentName: string = 'Opponent';
  
  private handlers: Record<string, P2PEventHandler[]> = {};
  private messageBuffer: P2PMessage[] = [];

  constructor() {
    window.addEventListener('beforeunload', () => {
      this.disconnect();
    });
  }

  /**
   * Initializes a Peer instance with a specific or random ID.
   */
  private initPeer(id?: string): Promise<string> {
    return new Promise((resolve, reject) => {
      // Create a PeerJS instance connecting to the public free signaling server
      this.peer = new Peer(id as string, {
        debug: 2,
      });

      this.peer.on('open', (assignedId) => {
        this.peerId = assignedId;
        console.log('[P2P] Peer open. My ID:', assignedId);
        resolve(assignedId);
      });

      this.peer.on('error', (err) => {
        console.error('[P2P] Peer error:', err);
        reject(err);
      });
    });
  }

  /**
   * Host a new game with a short readable code.
   */
  public async hostGame(playerName: string): Promise<string> {
    this.messageBuffer = [];
    this.isHost = true;
    this.playerName = playerName;
    
    // Generate a short 4-digit code (e.g., 1234)
    const code = Math.floor(1000 + Math.random() * 9000).toString();
    const fullId = `MATHILLERY-${code}`;
    
    await this.initPeer(fullId);

    if (this.peer) {
      this.peer.on('connection', (conn) => {
        console.log('[P2P] Incoming connection from:', conn.peer);
        if (this.connection) {
          // Already have a connection, reject new ones
          conn.close();
          return;
        }

        this.setupConnection(conn);
      });
    }
    
    return code;
  }

  /**
   * Join an existing game using the short code.
   */
  public async joinGame(code: string, playerName: string): Promise<void> {
    this.messageBuffer = [];
    this.isHost = false;
    this.playerName = playerName;
    
    await this.initPeer(); // random ID for client

    return new Promise((resolve, reject) => {
      if (!this.peer) return reject('No peer');

      const targetId = `MATHILLERY-${code.trim()}`;
      console.log('[P2P] Connecting to Host:', targetId);
      
      const conn = this.peer.connect(targetId, {
        reliable: true
      });

      conn.on('open', () => {
        console.log('[P2P] Connected to Host.');
        this.setupConnection(conn);
        resolve();
      });

      conn.on('error', (err) => {
        reject(err);
      });
    });
  }

  /**
   * Configures data and close events for an active connection.
   */
  private setupConnection(conn: DataConnection): void {
    this.connection = conn;

    conn.on('data', (data: unknown) => {
      const msg = data as P2PMessage;
      if (msg && msg.type) {
        this.emit(msg.type, msg.payload);
      }
    });

    conn.on('close', () => {
      console.log('[P2P] Connection closed.');
      this.emit('DISCONNECT', null);
      this.connection = null;
    });

    conn.on('error', (err) => {
      console.error('[P2P] Connection error:', err);
      this.emit('DISCONNECT', null);
      this.connection = null;
    });
    
    // Immediately exchange names over a custom internal message?
    // We can just use INIT_MATCH and another message, but let's do a simple handshake
    if (!this.isHost) {
      this.send('HANDSHAKE', { name: this.playerName });
    }
    this.on('HANDSHAKE', (payload) => {
      this.opponentName = payload.name;
      if (this.isHost) {
        this.send('HANDSHAKE', { name: this.playerName });
      }
    });
  }

  /**
   * Send a typed message to the peer.
   */
  public send(type: P2PMessageType | 'HANDSHAKE' | 'DISCONNECT', payload: any): void {
    if (this.connection && this.connection.open) {
      this.connection.send({ type, payload });
    } else {
      console.warn('[P2P] Cannot send message, connection not open.');
    }
  }

  /**
   * Register a listener for a specific message type.
   */
  public on(type: P2PMessageType | 'HANDSHAKE' | 'DISCONNECT', handler: P2PEventHandler): void {
    if (!this.handlers[type]) {
      this.handlers[type] = [];
    }
    this.handlers[type].push(handler);

    // Flush buffered messages for this type
    this.messageBuffer = this.messageBuffer.filter(msg => {
      if (msg.type === type) {
        handler(msg.payload);
        return false;
      }
      return true;
    });
  }

  /**
   * Remove a listener.
   */
  public off(type: string, handler: P2PEventHandler): void {
    if (!this.handlers[type]) return;
    this.handlers[type] = this.handlers[type].filter(h => h !== handler);
  }

  private emit(type: string, payload: any): void {
    if (this.handlers[type] && this.handlers[type].length > 0) {
      this.handlers[type].forEach(h => h(payload));
    } else if (type !== 'DISCONNECT') {
      this.messageBuffer.push({ type: type as any, payload });
    }
  }

  public disconnect(): void {
    try {
      this.send('DISCONNECT', null);
    } catch(e) {}
    
    this.handlers = {};
    this.messageBuffer = [];

    if (this.connection) {
      this.connection.close();
      this.connection = null;
    }
    if (this.peer) {
      this.peer.destroy();
      this.peer = null;
    }
    this.handlers = {};
  }
}

export const p2pManager = new P2PManager();
